import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { facturationService, patientService, consultationService } from '../../services/api';
import { Facture, Patient, Consultation, PaginatedResponse, Paiement } from '../../types';
import { X, Plus, CreditCard } from 'lucide-react';
import PaiementModal from './PaiementModal';
import PatientSearchSelect from '../common/PatientSearchSelect';

interface FactureFormProps {
  facture?: Facture;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FactureFormData {
  patient: number;
  consultation?: number;
  date_emission: string;
  date_echeance: string;
  montant_actes: number;
  montant_supplementaire: number;
  montant_total: number;
  pourcentage_remise: number;       // NOUVEAU
  montant_remise: number;           // NOUVEAU
  pourcentage_assurance: number;
  montant_assurance: number;
  montant_patient: number;
  notes?: string;
}

interface FactureBackendData {
  patient: number;
  consultation?: number;
  date_emission: string;
  date_echeance: string;
  montant_actes: number;
  montant_supplementaire: number;
  montant_total: number;
  pourcentage_remise: number;       // NOUVEAU
  montant_remise: number;           // NOUVEAU
  pourcentage_assurance: number;
  montant_assurance: number;
  montant_patient: number;
  notes?: string;
}

const FactureForm: React.FC<FactureFormProps> = ({ facture, onClose, onSuccess }) => {
  const [showPaiementModal, setShowPaiementModal] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<FactureFormData>({
    defaultValues: facture ? {
      patient: facture.patient,
      consultation: facture.consultation || undefined,
      date_emission: facture.date_emission,
      date_echeance: facture.date_echeance,
      montant_actes: facture.montant_actes || 0,
      montant_supplementaire: facture.montant_supplementaire || 0,
      montant_total: facture.montant_total,
      pourcentage_remise: facture.pourcentage_remise || 0,     // NOUVEAU
      montant_remise: facture.montant_remise || 0,             // NOUVEAU
      pourcentage_assurance: facture.pourcentage_assurance || 0,
      montant_assurance: facture.montant_assurance,
      montant_patient: facture.montant_patient,
      notes: facture.notes || ''
    } : {
      date_emission: new Date().toISOString().split('T')[0],
      date_echeance: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +30 jours
      montant_actes: 0,
      montant_supplementaire: 0,
      montant_total: 0,
      pourcentage_remise: 0,            // NOUVEAU
      montant_remise: 0,                // NOUVEAU
      pourcentage_assurance: 0,
      montant_assurance: 0
    }
  });

  const montantActes = watch('montant_actes');
  const montantSupplementaire = watch('montant_supplementaire');
  const montantTotal = watch('montant_total');
  const pourcentageRemise = watch('pourcentage_remise');         // NOUVEAU
  const montantRemise = watch('montant_remise');                 // NOUVEAU
  const pourcentageAssurance = watch('pourcentage_assurance');
  const montantAssurance = watch('montant_assurance');
  const selectedConsultationId = watch('consultation');
  const selectedPatientId = watch('patient');

  // Récupération du patient sélectionné (chargé à la demande)
  const { data: selectedPatient } = useQuery<Patient>({
    queryKey: ['patient', selectedPatientId],
    queryFn: () => patientService.getById(selectedPatientId!).then(res => res.data),
    enabled: !!selectedPatientId
  });

  // Récupération des consultations du patient sélectionné
  const { data: consultationsResponse } = useQuery<Consultation[] | PaginatedResponse<Consultation>>({
    queryKey: ['consultations', selectedPatientId],
    queryFn: async () => {
      if (!selectedPatientId) {
        return { results: [], count: 0 };
      }
      const response = await consultationService.getByPatient(selectedPatientId);
      return response.data;
    },
    enabled: !!selectedPatientId
  });

  // Trier les consultations par date décroissante (plus récente en premier)
  const consultations = React.useMemo(() => {
    if (!consultationsResponse) return [];

    // Gérer le cas où la réponse est un tableau ou un objet paginé
    const results = Array.isArray(consultationsResponse)
      ? consultationsResponse
      : (consultationsResponse.results || []);

    return [...results].sort((a, b) => {
      const dateA = new Date(a.date_consultation).getTime();
      const dateB = new Date(b.date_consultation).getTime();
      return dateB - dateA; // Tri décroissant
    });
  }, [consultationsResponse]);


  // Mutation pour créer/modifier une facture
  const factureMutation = useMutation<Facture, Error, FactureBackendData>({
    mutationFn: (data) => facture
      ? facturationService.updateFacture(facture.id, data).then(res => res.data)
      : facturationService.createFacture(data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['factures'] });
      queryClient.invalidateQueries({ queryKey: ['facture', facture?.id] });
      onSuccess?.();
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      const detail = data && typeof data === 'object'
        ? Object.values(data).flat().join('\n')
        : "Erreur lors de l'enregistrement de la facture.";
      alert(detail);
    }
  });


  // Effet pour auto-remplir le montant des actes quand une consultation est sélectionnée
  useEffect(() => {
    if (selectedConsultationId && consultations.length > 0) {
      const selectedConsultation = consultations.find(c => c.id === Number(selectedConsultationId));
      if (selectedConsultation) {
        // Calculer le montant total des actes médicaux
        const montantTotalActes = selectedConsultation.treatments?.reduce((total, treatment) => {
          const quantite = Number(treatment.quantite) || 0;
          const prixUnitaire = Number(treatment.prix_unitaire) || 0;
          const montantTraitement = quantite * prixUnitaire;
          return total + montantTraitement;
        }, 0) || 0;

        // Auto-remplir le montant des actes
        setValue('montant_actes', montantTotalActes);
      }
    }
  }, [selectedConsultationId, consultations, setValue]);

  // Effet pour calculer automatiquement le montant total
  useEffect(() => {
    const actes = Number(montantActes) || 0;
    const supplementaire = Number(montantSupplementaire) || 0;
    const total = actes + supplementaire;
    setValue('montant_total', total);
  }, [montantActes, montantSupplementaire, setValue]);

  // NOUVEAU: Effet pour calculer automatiquement le montant de remise
  useEffect(() => {
    const total = Number(montantTotal) || 0;
    const pourcentage = Number(pourcentageRemise) || 0;
    const montantRemiseCalcule = (total * pourcentage) / 100;
    setValue('montant_remise', montantRemiseCalcule);
  }, [montantTotal, pourcentageRemise, setValue]);

  // Effet pour calculer automatiquement le montant d'assurance basé sur le pourcentage
  // La remise est appliquée AVANT l'assurance
  useEffect(() => {
    const total = Number(montantTotal) || 0;
    const remise = Number(montantRemise) || 0;
    const montantApresRemise = total - remise;
    const pourcentage = Number(pourcentageAssurance) || 0;
    const montantAssuranceCalcule = (montantApresRemise * pourcentage) / 100;
    setValue('montant_assurance', montantAssuranceCalcule);
  }, [montantTotal, montantRemise, pourcentageAssurance, setValue]);

  // Effet pour pré-remplir le pourcentage d'assurance du patient sélectionné
  // ou le remettre à 0 si le patient n'est pas assuré
  useEffect(() => {
    if (selectedPatient) {
      if (selectedPatient.assureur_details) {
        if (!facture) {
          // Pré-remplir le pourcentage seulement pour les nouvelles factures
          setValue('pourcentage_assurance', selectedPatient.assureur_details.taux_couverture);
        }
      } else {
        // Patient non assuré: remettre à 0
        setValue('pourcentage_assurance', 0);
        setValue('montant_assurance', 0);
      }
    }
  }, [selectedPatient, setValue, facture]);

  // Effet pour pré-sélectionner la consultation la plus récente du patient
  useEffect(() => {
    if (selectedPatientId && consultations.length > 0 && !facture) {
      // La première consultation est déjà la plus récente grâce au tri
      const consultationPlusRecente = consultations[0];
      setValue('consultation', consultationPlusRecente.id);
    }
  }, [selectedPatientId, consultations, setValue, facture]);

  const onSubmit: SubmitHandler<FactureFormData> = async (data) => {
    try {
      // Convertir tous les montants en nombres
      const montantTotal = Number(data.montant_total) || 0;
      const montantRemise = Number(data.montant_remise) || 0;              // NOUVEAU
      const montantAssurance = Number(data.montant_assurance) || 0;
      const montantPatientCalcule = montantTotal - montantRemise - montantAssurance;  // MODIFIÉ
      
      // Préparer les données pour le backend (inclure maintenant montant_actes et montant_supplementaire)
      const factureData: FactureBackendData = {
        patient: Number(data.patient),
        consultation: data.consultation ? Number(data.consultation) : undefined,
        date_emission: data.date_emission,
        date_echeance: data.date_echeance,
        montant_actes: Number(data.montant_actes) || 0,
        montant_supplementaire: Number(data.montant_supplementaire) || 0,
        montant_total: montantTotal,
        pourcentage_remise: Number(data.pourcentage_remise) || 0,    // NOUVEAU
        montant_remise: montantRemise,                                // NOUVEAU
        pourcentage_assurance: Number(data.pourcentage_assurance) || 0,
        montant_assurance: Number(data.montant_assurance) || 0,
        montant_patient: montantPatientCalcule,
        notes: data.notes
      };
      
      await factureMutation.mutateAsync(factureData);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };


  const montantPaye = facture?.paiements?.reduce((sum, p) => {
    const montant = Number(p.montant) || 0;
    return sum + montant;
  }, 0) || 0;
  
  const montantPatientFacture = Number(facture?.montant_patient) || 0;
  const montantRestant = facture ? Math.max(0, montantPatientFacture - montantPaye) : 0;

  return (
    <div className="max-w-4xl mx-auto son-panel p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {facture ? `Facture ${facture.numero}` : 'Nouvelle facture'}
          </h2>
          {facture?.is_locked && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 mt-2">
              🔒 Verrouillée
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          title="Fermer"
          aria-label="Fermer"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Message d'avertissement pour factures verrouillées */}
      {facture?.is_locked && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Cette facture est verrouillée car au moins un paiement a été enregistré.
                Vous ne pouvez plus modifier les montants, mais vous pouvez toujours ajouter des paiements et l'imprimer.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Informations générales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient *
            </label>
            <PatientSearchSelect
              value={selectedPatientId}
              onChange={(patientId) => {
                setValue('patient', patientId || 0);
              }}
              error={errors.patient?.message}
              placeholder="Rechercher un patient par nom, prénom ou téléphone..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Consultation (optionnel)
            </label>
            <select
              {...register('consultation')}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
              title="Sélectionner une consultation"
              disabled={!selectedPatientId}
            >
              <option value="">
                {selectedPatientId ? 'Aucune consultation liée' : 'Sélectionnez d\'abord un patient'}
              </option>
              {consultations.map(consultation => (
                <option key={consultation.id} value={consultation.id}>
                  {new Date(consultation.date_consultation).toLocaleDateString('fr-FR')} - {consultation.motif_consultation}
                </option>
              ))}
            </select>
            {selectedPatientId && consultations.length > 0 && (
              <p className="text-green-600 text-sm mt-1">
                ✓ {consultations.length} consultation(s) trouvée(s) - La plus récente est sélectionnée
              </p>
            )}
            {selectedPatientId && consultations.length === 0 && (
              <p className="text-gray-500 text-sm mt-1">
                Aucune consultation trouvée pour ce patient
              </p>
            )}
            {selectedConsultationId && (
              <p className="text-blue-600 text-sm mt-1">
                ℹ️ Le montant des actes sera automatiquement rempli
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date d'émission *
            </label>
            <input
              type="date"
              {...register('date_emission', { required: 'La date d\'émission est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
              title="Date d'émission"
              placeholder="Date d'émission"
              aria-label="Date d'émission"
            />
            {errors.date_emission && <p className="text-red-500 text-sm mt-1">{errors.date_emission.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date d'échéance *
            </label>
            <input
              type="date"
              {...register('date_echeance', { required: 'La date d\'échéance est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
              title="Date d'échéance"
              placeholder="Date d'échéance"
              aria-label="Date d'échéance"
            />
            {errors.date_echeance && <p className="text-red-500 text-sm mt-1">{errors.date_echeance.message}</p>}
          </div>
        </div>

        {/* Montants */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-800 border-b pb-2">Détail des montants</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant des actes médicaux {selectedConsultationId && <span className="text-green-600 text-xs">(Auto-calculé)</span>}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...register('montant_actes')}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
                title="Montant des actes médicaux"
                placeholder="0 GNF"
                aria-label="Montant des actes médicaux"
                readOnly={!!selectedConsultationId}
              />
              {selectedConsultationId && (
                <p className="text-blue-600 text-xs mt-1">
                  📋 Calculé automatiquement depuis la consultation
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant supplémentaire
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...register('montant_supplementaire')}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
                title="Montant supplémentaire"
                placeholder="0 GNF"
                aria-label="Montant supplémentaire"
              />
              <p className="text-gray-500 text-xs mt-1">
                Frais supplémentaires, médicaments, etc.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant total * <span className="text-green-600 text-xs">(Auto-calculé)</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...register('montant_total', { required: 'Le montant total est requis' })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500 bg-gray-50"
                title="Montant total"
                placeholder="0 GNF"
                aria-label="Montant total"
                readOnly
              />
              {errors.montant_total && <p className="text-red-500 text-sm mt-1">{errors.montant_total.message}</p>}
              <p className="text-green-600 text-xs mt-1">
                {((montantActes || 0) + (montantSupplementaire || 0)).toLocaleString()} GNF
              </p>
            </div>

            {/* NOUVEAU: Champ Remise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remise (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  {...register('pourcentage_remise')}
                  className="w-full px-3 py-2 pr-8 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
                  title="Pourcentage de remise"
                  placeholder="0"
                  aria-label="Pourcentage de remise"
                  disabled={facture?.is_locked}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="text-gray-500 text-xs mt-1">
                La remise est appliquée avant la prise en charge assurance
              </p>
            </div>

            {/* NOUVEAU: Montant Remise (calculé) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant remise <span className="text-green-600 text-xs">(Auto-calculé)</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...register('montant_remise')}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500 bg-gray-50"
                title="Montant de la remise"
                placeholder="0 GNF"
                aria-label="Montant de la remise"
                readOnly
              />
              <p className="text-green-600 text-xs mt-1">
                {(Number(montantRemise) || 0).toLocaleString()} GNF
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pourcentage assurance
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  {...register('pourcentage_assurance')}
                  className={`w-full px-3 py-2 pr-8 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500 ${
                    !selectedPatient?.assureur_details ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''
                  }`}
                  title="Pourcentage assurance"
                  placeholder="0"
                  aria-label="Pourcentage assurance"
                  disabled={facture?.is_locked || !selectedPatient?.assureur_details}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
              </div>
              {selectedPatient?.assureur_details ? (
                <p className="text-blue-600 text-xs mt-1">
                  Taux de couverture standard: {selectedPatient.assureur_details.taux_couverture}%
                </p>
              ) : selectedPatient ? (
                <p className="text-orange-500 text-xs mt-1">
                  Ce patient n'est pas assuré — champ verrouillé
                </p>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant assurance <span className="text-green-600 text-xs">(Auto-calculé)</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...register('montant_assurance')}
                className={`w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500 bg-gray-50 ${
                  !selectedPatient?.assureur_details ? 'opacity-60' : ''
                }`}
                title="Montant assurance"
                placeholder="0 GNF"
                aria-label="Montant assurance"
                readOnly
              />
              <p className="text-green-600 text-xs mt-1">
                {(Number(montantAssurance) || 0).toLocaleString()} GNF
              </p>
            </div>
          </div>
          {/* Résumé des montants */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Montant total:</span>
              <span className="font-semibold">{(montantTotal || 0).toLocaleString()} GNF</span>
            </div>
            {pourcentageRemise > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Remise ({pourcentageRemise}%):</span>
                <span>-{(montantRemise || 0).toLocaleString()} GNF</span>
              </div>
            )}
            {pourcentageAssurance > 0 && (
              <div className="flex justify-between text-sm text-blue-600">
                <span>Assurance ({pourcentageAssurance}%):</span>
                <span>-{(montantAssurance || 0).toLocaleString()} GNF</span>
            </div>
            )}
            <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
              <span>À payer par le patient:</span>
              <span className="text-blue-700">
                {((montantTotal || 0) - (montantRemise || 0) - (montantAssurance || 0)).toLocaleString()} GNF
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-gray-900 focus:ring-teal-500"
              placeholder="Notes complémentaires..."
              title="Notes complémentaires"
            />
          </div>
        </div>

        {/* Section Paiements (uniquement pour les factures existantes) */}
        {facture && (
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Paiements</h3>
              {montantRestant > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPaiementModal(true)}
                  className="flex items-center px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter un paiement
                </button>
              )}
            </div>

            {/* Résumé des paiements */}
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Montant payé</p>
                <p className="text-lg font-semibold text-green-600">{(montantPaye || 0).toLocaleString()} GNF</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Montant restant</p>
                <p className="text-lg font-semibold text-red-600">{(montantRestant || 0).toLocaleString()} GNF</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Statut</p>
                <p className={`text-lg font-semibold ${
                  montantRestant <= 0 ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {montantRestant <= 0 ? 'Payé' : 'En attente'}
                </p>
              </div>
            </div>

            {/* Liste des paiements */}
            {facture.paiements && facture.paiements.length > 0 && (
              <div className="space-y-2">
                {facture.paiements.map((paiement) => (
                  <div key={paiement.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {(Number(paiement.montant) || 0).toLocaleString()} GNF - {paiement.mode_paiement}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(paiement.date_paiement).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    {paiement.reference && (
                      <p className="text-sm text-gray-600">Réf: {paiement.reference}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={factureMutation.isLoading}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            {factureMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Modal de paiement */}
      {showPaiementModal && facture && (
        <PaiementModal
          factureId={facture.id}
          montantRestant={montantRestant}
          dateMin={facture.date_paiement_min}
          onClose={() => setShowPaiementModal(false)}
          onSuccess={() => {
            setShowPaiementModal(false);
            queryClient.invalidateQueries({ queryKey: ['factures'] });
            queryClient.invalidateQueries({ queryKey: ['facture', facture.id] });

            // Proposer d'ajouter un paiement consécutif
            const newMontantRestant = montantRestant - (facture.montant_paye || 0);
            if (newMontantRestant > 0) {
              const addAnother = window.confirm(
                `Paiement enregistré avec succès!\n\nMontant restant: ${newMontantRestant.toLocaleString()} GNF\n\nVoulez-vous ajouter un autre paiement?`
              );
              if (addAnother) {
                setShowPaiementModal(true);
              }
            }

            if (onSuccess) {
              onSuccess();
            }
          }}
        />
      )}
    </div>
  );
};

export default FactureForm;